import { useMemo, useState } from "react";
import SearchBar from "../ui/SearchBar";
import Alert from "../ui/alert/Alert";
import { PageLoadingView } from "../common/PageLoadingView";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { DropdownItem } from "../ui/dropdown/DropdownItem";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../ui/table";
import { useMessageHistory } from "../../hooks/useMessageHistory";
import { useMessageTypes } from "../../hooks/useMessageTypes";
import { useUser } from "../../context/UserInfoProvider";
import { ChevronDownIcon } from "../../icons";
import { ChevronLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";

const formatBackendTimestampToLocal = (createdAt: string | null | undefined) => {
  if (!createdAt || !createdAt.trim()) return "N/A";

  let normalized = createdAt.trim();

  if (!normalized.includes("T") && normalized.includes(" ")) {
    normalized = normalized.replace(" ", "T");
  }

  normalized = normalized.replace(/\+00$/, "Z");
  normalized = normalized.replace(/(\.\d{3})\d+(Z|[+-]\d{2}:?\d{2})$/, "$1$2");
  normalized = normalized.replace(/([+-]\d{2})$/, "$1:00");

  const parsedDate = new Date(normalized);
  if (Number.isNaN(parsedDate.getTime())) return "N/A";

  return parsedDate.toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export default function MessageHistory() {
  const { hotelId } = useUser();
  const { messageTypes } = useMessageTypes();
  const [searchInput, setSearchInput] = useState("");
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);

  const {
    messages,
    isLoading,
    error,
    pagination,
    setPage,
    setLimit,
    refetch,
    filters,
    setType,
    setSearchTerm,
  } = useMessageHistory({
    hotelId: hotelId || "",
    initialPage: 1,
    initialLimit: 20,
  });

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((pagination.total || 0) / pagination.limit)),
    [pagination.total, pagination.limit]
  );
  const hasMore = pagination.page < totalPages;
  const selectedMessage = useMemo(
    () => messages.find((message) => message.messageId === selectedMessageId) || null,
    [messages, selectedMessageId]
  );
  const selectedMessageIndex = useMemo(
    () =>
      selectedMessage
        ? messages.findIndex((message) => message.messageId === selectedMessage.messageId)
        : -1,
    [messages, selectedMessage]
  );

  const getRowClassName = () => {
    return "bg-white dark:bg-black";
  };

  const getFrameClass = () => {
    return "dark:bg-black";
  };

  if (!hotelId) {
    return <PageLoadingView />;
  }

  return (
    <div className="flex flex-col h-full p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-white mb-2">
          Message History
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Review all guest messages with search and filter controls.
        </p>
      </div>

      {error && (
        <div className="mb-4">
          <Alert
            variant="error"
            title="Error Loading Message History"
            message={error.message || "Could not load message history."}
          />
        </div>
      )}

      <div className="mb-4 flex flex-wrap md:flex-nowrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[300px]">
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="p-2 rounded-md border border-gray-200 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-800 dark:hover:bg-gray-700"
            aria-label="Refresh"
          >
            <svg
              className="w-5 h-5 text-gray-600 dark:text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
          <div className="w-full max-w-[620px]">
            <SearchBar
              searchQuery={searchInput}
              onSearchChange={(value) => {
                setSearchInput(value);
                if (value === "") {
                  setSearchTerm("");
                }
              }}
              onSearchSubmit={() => {
                setSearchTerm(searchInput);
                setPage(1);
              }}
              isLoading={isLoading}
              placeholder="Search by text, sender ID, receiver ID, or request ID..."
            />
          </div>
        </div>

        <div className="relative inline-block w-full md:w-56 shrink-0">
          <button
            className="flex dropdown-toggle w-full items-center justify-between 
            px-4 py-2 text-sm text-black bg-white border rounded-md
            hover:bg-gray-100 dark:border-gray-800 dark:text-white/90 dark:bg-white/[0.03]"
            onClick={() => setIsTypeDropdownOpen((prev) => !prev)}
          >
            <span className="px-2">{filters.type || "All Types"}</span>
            <ChevronDownIcon className="w-4 h-4 text-black dark:text-white/90" />
          </button>
          <Dropdown
            isOpen={isTypeDropdownOpen}
            onClose={() => setIsTypeDropdownOpen(false)}
            className="w-full absolute z-100"
          >
            <DropdownItem
              className="dark:text-white/90"
              onItemClick={() => {
                setType("");
                setIsTypeDropdownOpen(false);
              }}
            >
              All Types
            </DropdownItem>
            {messageTypes.map((messageType) => (
              <DropdownItem
                key={messageType.typeid}
                className="dark:text-white/90"
                onItemClick={() => {
                  setType(messageType.type);
                  setIsTypeDropdownOpen(false);
                }}
              >
                {messageType.type}
              </DropdownItem>
            ))}
          </Dropdown>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-56">
            <PageLoadingView />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-56 text-sm text-gray-500 dark:text-gray-400">
            No messages found for the selected filters.
          </div>
        ) : (
          <Table className="w-full">
            <TableHeader className="border-gray-200 border-b-[3px] dark:border-gray-800">
              <TableRow>
                <TableCell
                  isHeader
                  className="py-3 pl-2 pr-2 font-light text-[0.875rem] text-black text-start dark:text-gray-400 w-56"
                >
                  Timestamp
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 pl-2 pr-1 font-light text-[0.875rem] text-black text-start dark:text-gray-400 w-52"
                >
                  Sender
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 pl-2 pr-1 font-light text-[0.875rem] text-black text-start dark:text-gray-400 w-52"
                >
                  Receiver
                </TableCell>
                <TableCell
                  isHeader
                  className="py-3 pl-2 pr-1 font-light text-[0.875rem] text-black text-start dark:text-gray-400 w-36"
                >
                  Message Title
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody className="divide-y-[1px] divide-gray-200 dark:divide-gray-800">
              {messages.map((message) => (
                <TableRow
                  key={message.messageId}
                  className={`${getRowClassName()} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 ${
                    selectedMessage?.messageId === message.messageId
                      ? "bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500"
                      : ""
                  }`}
                  onClick={() => setSelectedMessageId(message.messageId)}
                >
                  <TableCell
                    className={`py-3 pl-2 pr-2 text-[#1100D8] text-[0.875rem] font-light dark:text-white w-56 ${getFrameClass()}`}
                  >
                    {formatBackendTimestampToLocal(message.createdAt)}
                  </TableCell>
                  <TableCell
                    className={`py-3 pl-2 pr-1 text-[#1100D8] text-[0.875rem] font-light dark:text-white w-52 ${getFrameClass()}`}
                  >
                    {message.sender?.name || "System/Unknown"}
                  </TableCell>
                  <TableCell
                    className={`py-3 pl-2 pr-1 text-[#1100D8] text-[0.875rem] font-light dark:text-white w-52 ${getFrameClass()}`}
                  >
                    {message.receiver?.name || "N/A"}
                  </TableCell>
                  <TableCell
                    className={`py-3 pl-2 pr-1 text-[#1100D8] text-[0.875rem] font-light dark:text-white w-36 ${getFrameClass()}`}
                  >
                    {message.type || "N/A"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Show</span>
          <select
            value={pagination.limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="border rounded px-3 py-1 text-sm bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white pr-8"
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            items per page
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setPage(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-700 dark:text-white dark:bg-gray-800"
          >
            Previous
          </button>
          <span className="text-sm dark:text-gray-400">
            Page {pagination.page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(pagination.page + 1)}
            disabled={!hasMore}
            className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-700 dark:text-white dark:bg-gray-800"
          >
            Next
          </button>
        </div>
      </div>

      {selectedMessage && (
        <div
          aria-hidden
          className="fixed inset-0 z-40 top-0 left-0 right-0 bottom-0"
          onClick={() => setSelectedMessageId(null)}
        />
      )}

      <div
        className={`
          fixed right-0 top-[70px] w-[30%] h-[calc(100vh-70px)] z-50
          px-5 py-5 shadow-[0_0_15px_rgba(0,0,0,0.1)] dark:shadow-[0_0_15px_rgba(255,255,255,0.1)] 
          bg-white dark:bg-black overflow-y-auto 
          transition-transform duration-300 ease-in-out transform
          ${selectedMessage ? "translate-x-0" : "translate-x-full"}
        `}
      >
        {selectedMessage && (
          <>
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    if (selectedMessageIndex > 0) {
                      setSelectedMessageId(messages[selectedMessageIndex - 1].messageId);
                    }
                  }}
                  disabled={selectedMessageIndex <= 0}
                  className="text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full p-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Previous message"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    if (selectedMessageIndex < messages.length - 1) {
                      setSelectedMessageId(messages[selectedMessageIndex + 1].messageId);
                    }
                  }}
                  disabled={selectedMessageIndex >= messages.length - 1}
                  className="text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full p-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Next message"
                >
                  <ChevronLeftIcon className="w-5 h-5 rotate-180" />
                </button>
              </div>
              <button
                onClick={() => setSelectedMessageId(null)}
                className="text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full p-2 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 pt-2">
              <div>
                <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                  Timestamp
                </h3>
                <p className="text-sm text-gray-900 dark:text-white">
                  {formatBackendTimestampToLocal(selectedMessage.createdAt)}
                </p>
              </div>

              <div>
                <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                  Sender
                </h3>
                <p className="text-sm text-gray-900 dark:text-white">
                  {selectedMessage.sender?.name || "System/Unknown"}
                </p>
              </div>

              <div>
                <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                  Receiver
                </h3>
                <p className="text-sm text-gray-900 dark:text-white">
                  {selectedMessage.receiver?.name || "N/A"}
                </p>
              </div>

              <div>
                <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                  Message Title
                </h3>
                <p className="text-sm text-gray-900 dark:text-white">
                  {selectedMessage.type || "N/A"}
                </p>
              </div>

              <div>
                <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                  Subject
                </h3>
                <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                  {selectedMessage.subject || "N/A"}
                </p>
              </div>

              <div>
                <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                  Message Content
                </h3>
                <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                  {selectedMessage.content || "N/A"}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
